// AdminJobDetail.tsx — /admin/jobs/:id (Jobs v2, Task 3 of the admin-v2 cycle, spec
// docs/superpowers/specs/2026-08-06-admin-v2-internal-workspaces-design.md §B3). A PAGE, not a
// drawer: every other admin v2 detail view (workspace, bot) is already a full page, a job's
// full pretty-printed payload can run long, and a page is deep-linkable/shareable without any
// portal/overlay state to manage — keeping the whole v2 surface to one consistent "click a row,
// land on its own page" pattern beat a one-off drawer for this single case. `GET
// /api/admin/jobs/:id` is the one place the admin UI is allowed to render the FULL payload
// (already redacted server-side — see `AdminJobDetailDto`'s doc comment).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import type {
  AdminCancelJobResponseDto,
  AdminJobDetailDto,
  AdminRetryJobResponseDto,
} from '@repo/shared-types';
import { Button } from '@repo/ui/components/button';
import { Card } from '@repo/ui/components/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import { Skeleton, SkeletonText } from '@repo/ui/components/skeleton';
import { apiFetch, ApiError } from '../lib/api';
import { useLocale } from '../locales/context';
import { formatDateTime as formatDateTimeIntl } from '../locales/format';

/** Sized to the real header + facts card below (handoff Shell Anatomy §06). */
function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <SkeletonText className="w-28" />
      <div className="flex items-center gap-3">
        <Skeleton className="h-8 w-40" />
        <SkeletonText className="w-16" />
      </div>
      <Card className="gap-3 p-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="flex flex-col gap-1.5">
              <SkeletonText className="w-16" />
              <SkeletonText className="w-32" />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export function AdminJobDetail() {
  const { t, locale } = useLocale();
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data: job, isPending, error } = useQuery({
    queryKey: ['admin', 'job', id],
    queryFn: () => apiFetch<AdminJobDetailDto>(`/api/admin/jobs/${id}`),
  });

  const [actionError, setActionError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'job', id] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'jobs'] });
  }

  const retryJob = useMutation({
    mutationFn: () => apiFetch<AdminRetryJobResponseDto>(`/api/admin/jobs/${id}/retry`, { method: 'POST' }),
    onSuccess: () => {
      setActionError(null);
      invalidate();
    },
    onError: (err) =>
      setActionError(err instanceof ApiError ? err.message : t.admin.jobs.retryJobErrorFallback),
  });

  const cancelJob = useMutation({
    mutationFn: () => apiFetch<AdminCancelJobResponseDto>(`/api/admin/jobs/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      setActionError(null);
      setCancelDialogOpen(false);
      invalidate();
    },
    onError: (err) =>
      setActionError(err instanceof ApiError ? err.message : t.admin.jobs.cancelJobErrorFallback),
  });

  if (isPending) return <DetailSkeleton />;
  if (error) return <p className="text-destructive">{error.message}</p>;
  if (!job) return null;

  return (
    <div className="flex flex-col gap-6">
      <Link to="/admin/jobs" className="w-fit text-sm text-muted-foreground hover:text-foreground">
        {t.admin.jobs.backToJobs}
      </Link>

      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-2xl font-bold">{job.kind}</h1>
        <span className="text-sm text-muted-foreground">{job.status}</span>
      </div>

      <Card className="gap-3 p-5">
        <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">{t.admin.jobs.jobIdLabel}</span>
            <span className="font-mono text-xs">{job.id}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">{t.admin.jobs.attemptsLabel}</span>
            <span className="font-medium">{job.attempts}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">{t.admin.jobs.createdLabel}</span>
            <span className="font-medium">{formatDateTimeIntl(job.createdAt, locale)}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">{t.admin.jobs.updatedLabel}</span>
            <span className="font-medium">{formatDateTimeIntl(job.updatedAt, locale)}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">{t.admin.jobs.runAfterLabel}</span>
            <span className="font-medium">{formatDateTimeIntl(job.runAfter, locale)}</span>
          </div>
        </div>

        <div className="flex gap-2">
          {job.status === 'failed' && (
            <Button type="button" variant="outline" size="sm" disabled={retryJob.isPending} onClick={() => retryJob.mutate()}>
              {retryJob.isPending ? t.admin.jobs.retryingCta : t.admin.jobs.retryCta}
            </Button>
          )}
          {job.status === 'pending' && (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={cancelJob.isPending}
              onClick={() => setCancelDialogOpen(true)}
            >
              {t.admin.jobs.cancelCta}
            </Button>
          )}
        </div>
        {actionError && <p className="text-[13px] text-destructive">{actionError}</p>}
      </Card>

      {job.lastError && (
        <div className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold text-muted-foreground">{t.admin.jobs.errorHeading}</h2>
          <pre className="overflow-x-auto rounded-md border bg-muted p-4 font-mono text-xs whitespace-pre-wrap">
            {job.lastError}
          </pre>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-muted-foreground">{t.admin.jobs.payloadHeading}</h2>
        <pre className="overflow-x-auto rounded-md border bg-muted p-4 font-mono text-xs">
          {JSON.stringify(job.payload, null, 2)}
        </pre>
      </div>

      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.admin.jobs.cancelDialogTitle}</DialogTitle>
            <DialogDescription>{t.admin.jobs.cancelDialogDescriptionDetailPage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCancelDialogOpen(false)}>
              {t.admin.jobs.keepJobCta}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={cancelJob.isPending}
              onClick={() => cancelJob.mutate()}
            >
              {cancelJob.isPending ? t.admin.jobs.cancellingCta : t.admin.jobs.cancelJobCta}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
